import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Resolver } from 'react-hook-form';
import { useForm, SubmitHandler, Controller, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store/store';
import banner from '../assets/tpp-claims-banner.jpg';
import testPoliceReportPng from '../assets/test-police-report.png';
import { config } from '../config';
import {
  TextField,
  Button,
  Typography,
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import Layout from '../components/Layout';
import ProgressSteps from '../components/ProgressSteps';
import Step1CompleteOverlayDialog from '../components/Step1CompleteOverlayDialog';
import {
  acknowledgeStep1Overlay,
  hasAcknowledgedStep1Overlay,
} from '../utils/step1CompleteOverlay';
import { updateClaimField } from '../store/claimSlice';
import {
  getLossTypes,
  updateClaim,
  uploadClaimAttachments,
  getClaim,
  filterLawEnforcementAttachments,
  getClaimAttachmentPreviewUrl,
  deleteAllLawEnforcementAttachments,
  LossType,
  LawEnforcementAttachmentMeta,
} from '../services/api';
import { IncidentDateField } from '../components/IncidentDateField';
import { DateFieldWithCalendar, isDateOnOrBeforeToday } from '../components/DateFieldWithCalendar';
import { useSnackbar } from '../contexts/SnackbarContext';
import { getErrorMessage } from '../utils/errorHandler';

type FormData = {
  incident_date: string;
  tpp_loss_type_id: string;
  other_loss_type_description: string;
  authority_name: string;
  authority_filed_report: string;
  items_current_location: string;
  info_manager_notified: string;
  another_claim_filed: string;
  another_claim_loss_type_id: string;
  another_claim_date: string;
  law_enforcement_reports?: FileList;
};

const toYyyyMmDd = (v: string): string | null => {
  const s = String(v || '').trim();
  if (s.includes('/')) {
    const p = s.split('/').map((x) => parseInt(x, 10));
    if (p.length === 3 && !p.some(isNaN) && p[2] >= 1000) {
      return `${p[2]}-${String(p[0]).padStart(2, '0')}-${String(p[1]).padStart(2, '0')}`;
    }
  } else if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return null;
};

const isTheftOrVandalismLossType = (lossTypeId: string | undefined, lossTypes: LossType[]): boolean => {
  if (!lossTypeId || lossTypes.length === 0) return false;
  const lt = lossTypes.find((t) => String(t.id) === String(lossTypeId));
  const n = (lt?.name || '').toLowerCase();
  return n.includes('theft') || n.includes('vandalism');
};

const isPestLossType = (lossTypeId: string | undefined, lossTypes: LossType[]): boolean => {
  if (!lossTypeId || lossTypes.length === 0) return false;
  const lt = lossTypes.find((t) => String(t.id) === String(lossTypeId));
  const n = (lt?.name || '').toLowerCase();
  return n.includes('pest');
};

const isOtherLossType = (lossTypeId: string | undefined, lossTypes: LossType[]): boolean => {
  if (!lossTypeId || lossTypes.length === 0) return false;
  const lt = lossTypes.find((t) => String(t.id) === String(lossTypeId));
  return (lt?.name || '').trim().toLowerCase() === 'other';
};

const fileListsMatch = (a: FileList | null, b: FileList | null): boolean => {
  if (!a || !b) return a === b;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const fa = a.item(i);
    const fb = b.item(i);
    if (!fa || !fb || fa.name !== fb.name || fa.size !== fb.size) return false;
  }
  return true;
};

type LawEnforcementThumbProps = { claimId: number; att: LawEnforcementAttachmentMeta };

const LawEnforcementReportThumb: React.FC<LawEnforcementThumbProps> = ({ claimId, att }) => {
  const url = getClaimAttachmentPreviewUrl(claimId, att.id, att.token);
  const looksLikeImage = /\.(jpe?g|png)$/i.test(att.original_filename);
  const [showImg, setShowImg] = useState(looksLikeImage);

  return (
    <Box sx={{ width: 140, textAlign: 'center' }}>
      {showImg ? (
        <Box
          component="img"
          src={url}
          alt={att.original_filename}
          onError={() => setShowImg(false)}
          sx={{
            width: '100%',
            maxHeight: 120,
            objectFit: 'contain',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
          }}
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            p: 1,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            minHeight: 120,
            justifyContent: 'center',
          }}
        >
          <InsertDriveFileIcon color="action" sx={{ fontSize: 48 }} />
        </Box>
      )}
      <Typography
        variant="caption"
        display="block"
        sx={{ mt: 0.5, wordBreak: 'break-word' }}
        title={att.original_filename}
      >
        {att.original_filename}
      </Typography>
    </Box>
  );
};

const buildIncidentSchema = (lossTypes: LossType[], existingLawReportCount: number) =>
  yup.object({
    incident_date: yup.string()
      .required('Incident date is required')
      .test('max-today', 'Incident date cannot be in the future', (value) => {
        if (!value || typeof value !== 'string') return true;
        const y = toYyyyMmDd(value) ?? value;
        if (!/^\d{4}-\d{2}-\d{2}$/.test(y)) return true;
        const t = new Date();
        const today = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
        return y <= today;
      }),
    tpp_loss_type_id: yup.string().required('Reason for Claim is required'),
    other_loss_type_description: yup.string().when('tpp_loss_type_id', {
      is: (val: unknown) => isOtherLossType(String(val ?? ''), lossTypes),
      then: (schema) =>
        schema.test(
          'other-loss-required',
          'Other loss type description is required',
          (value) => String(value ?? '').trim().length > 0
        ),
      otherwise: (schema) => schema.optional(),
    }),
    authority_name: yup
      .string()
      .test(
        'authority-name-theft',
        'Law enforcement name is required for Theft or Vandalism claims.',
        function (value) {
          const lossTypeId = this.parent?.tpp_loss_type_id as string | undefined;
          if (!isTheftOrVandalismLossType(lossTypeId, lossTypes)) return true;
          return String(value ?? '').trim().length > 0;
        }
      ),
    authority_filed_report: yup
      .string()
      .test(
        'authority-report-theft',
        'Filed report number is required for Theft or Vandalism claims.',
        function (value) {
          const lossTypeId = this.parent?.tpp_loss_type_id as string | undefined;
          if (!isTheftOrVandalismLossType(lossTypeId, lossTypes)) return true;
          return String(value ?? '').trim().length > 0;
        }
      ),
    items_current_location: yup.string().required('Current location of damaged items is required'),
    info_manager_notified: yup
      .string()
      .oneOf(['1', '0'], 'Please select whether the property manager was notified')
      .required('Please select whether the property manager was notified'),
    another_claim_filed: yup
      .string()
      .oneOf(['1', '0'], 'Please select whether you have filed any other claims')
      .required('Please select whether you have filed any other claims'),
    another_claim_loss_type_id: yup.string().when('another_claim_filed', {
      is: '1',
      then: (schema) => schema.required('Please select the type of loss'),
      otherwise: (schema) => schema.optional(),
    }),
    another_claim_date: yup.string().when('another_claim_filed', {
      is: '1',
      then: (schema) =>
        schema
          .required('Date of other claim filed is required')
          .test('max-today', 'Date of other claim filed must be on or before today', (value) => {
            if (!value || typeof value !== 'string') return false;
            return isDateOnOrBeforeToday(value);
          }),
      otherwise: (schema) => schema.optional(),
    }),
    law_enforcement_reports: yup.mixed().test(
      'law-files-theft',
      'Please upload at least one law enforcement report for Theft or Vandalism claims.',
      function (value) {
        const lossTypeId = this.parent?.tpp_loss_type_id as string | undefined;
        if (!isTheftOrVandalismLossType(lossTypeId, lossTypes)) return true;
        if (existingLawReportCount > 0) return true;
        return value instanceof FileList && value.length > 0;
      }
    ),
  }).required();

const Incident: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { showError } = useSnackbar();
  const claimData = useSelector((state: RootState) => state.claim);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step1OverlayOpen, setStep1OverlayOpen] = useState(false);
  const [step1OverlayClaimNumber, setStep1OverlayClaimNumber] = useState('');
  const [lossTypes, setLossTypes] = useState<LossType[]>([]);
  const [existingLawReports, setExistingLawReports] = useState<LawEnforcementAttachmentMeta[]>([]);
  const [lawAttachmentsLoading, setLawAttachmentsLoading] = useState(false);
  const [deleteAllLawDialogOpen, setDeleteAllLawDialogOpen] = useState(false);
  const [isDeletingAllLaw, setIsDeletingAllLaw] = useState(false);
  const [policeReportInfoDialogOpen, setPoliceReportInfoDialogOpen] = useState(false);
  const [pestPayoutDialogOpen, setPestPayoutDialogOpen] = useState(false);
  /** Keeps native file input UI in sync when `law_enforcement_reports` is set programmatically (Load Test Data). */
  const lawEnforcementFileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!claimData.claimId) {
      navigate('/', { replace: true });
      return;
    }
  }, [claimData.claimId, navigate]);

  useEffect(() => {
    const claimId = claimData.claimId;
    if (claimId == null) return;

    const navState = location.state as {
      showStep1CompleteOverlay?: boolean;
      fromProgressSteps?: boolean;
    } | null;

    if (navState?.fromProgressSteps) return;
    if (!navState?.showStep1CompleteOverlay) return;
    if (hasAcknowledgedStep1Overlay(claimId)) return;

    let cancelled = false;
    (async () => {
      try {
        const claim = await getClaim(claimId);
        if (cancelled) return;
        setStep1OverlayClaimNumber(String(claim?.claim_number ?? ''));
        setStep1OverlayOpen(true);
        navigate(location.pathname, { replace: true, state: {} });
      } catch {
        if (!cancelled) {
          setStep1OverlayClaimNumber('');
          setStep1OverlayOpen(true);
          navigate(location.pathname, { replace: true, state: {} });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [claimData.claimId, location.pathname, location.state, navigate]);

  const handleStep1OverlayOk = () => {
    const claimId = claimData.claimId;
    if (claimId != null) {
      acknowledgeStep1Overlay(claimId);
    }
    setStep1OverlayOpen(false);
  };

  useEffect(() => {
    const fetchLossTypes = async () => {
      try {
        const types = await getLossTypes();
        setLossTypes(types);
      } catch (error) {
        console.error('Error fetching loss types:', error);
      }
    };
    
    fetchLossTypes();
  }, []);

  const handleFieldChange = (field: string, value: string) => {
    dispatch(updateClaimField({ field: field as any, value }));
  };

  const validationSchema = useMemo(
    () => buildIncidentSchema(lossTypes, existingLawReports.length),
    [lossTypes, existingLawReports.length]
  );

  const yupResolverFn = useCallback(
    async (...args: Parameters<Resolver<FormData>>) => {
      const result = await (yupResolver(validationSchema as any) as Resolver<FormData>)(...args);
      return result;
    },
    [validationSchema]
  ) as Resolver<FormData>;

  const { handleSubmit, formState: { errors }, setValue, control, trigger } = useForm<FormData>({
    resolver: yupResolverFn,
    defaultValues: {
      incident_date: claimData.incident_date || '',
      tpp_loss_type_id: claimData.tpp_loss_type_id || '',
      other_loss_type_description: claimData.other_loss_type_description || '',
      authority_name: claimData.authority_name || '',
      authority_filed_report: claimData.authority_filed_report || '',
      items_current_location: claimData.items_current_location || '',
      info_manager_notified: claimData.info_manager_notified || '',
      another_claim_filed: claimData.another_claim_filed || '',
      another_claim_loss_type_id: claimData.another_claim_loss_type_id || '',
      another_claim_date: claimData.another_claim_date || '',
    },
  });

  const watchedLossTypeId = useWatch({ control, name: 'tpp_loss_type_id' });
  const watchedAnotherClaimFiled = useWatch({ control, name: 'another_claim_filed' });
  const watchedLawEnforcementFiles = useWatch({ control, name: 'law_enforcement_reports' });

  /**
   * When loss type switches to Theft/Vandalism, `lawAttachmentsLoading` briefly replaces the
   * file input with a spinner — so code that runs right after `fetch()` has no mounted input.
   * After loading finishes, mirror form state (`law_enforcement_reports`) onto the real input
   * so the browser shows the chosen file name instead of "No file chosen".
   */
  useEffect(() => {
    if (lawAttachmentsLoading || existingLawReports.length > 0) return;
    const input = lawEnforcementFileInputRef.current;
    const files = watchedLawEnforcementFiles;
    if (!input) return;
    if (!(files instanceof FileList) || files.length === 0) {
      input.value = '';
      return;
    }
    if (fileListsMatch(input.files, files)) return;
    try {
      input.files = files;
    } catch {
      // Safari may not allow assignment; filename still shown below the input via watch.
    }
  }, [lawAttachmentsLoading, existingLawReports.length, watchedLawEnforcementFiles]);

  const lossTypeIdInList = (id: string) =>
    lossTypes.some((type) => String(type.id) === String(id));

  const isTheftOrVandalism = isTheftOrVandalismLossType(watchedLossTypeId, lossTypes);
  const isOtherLoss = isOtherLossType(watchedLossTypeId, lossTypes);

  useEffect(() => {
    const claimId = claimData.claimId;
    if (!claimId) return;
    if (!isTheftOrVandalismLossType(watchedLossTypeId, lossTypes)) {
      return;
    }

    let cancelled = false;
    (async () => {
      setLawAttachmentsLoading(true);
      try {
        const claim = await getClaim(claimId);
        if (cancelled) return;
        setExistingLawReports(filterLawEnforcementAttachments(claim.attachments));
      } catch (e) {
        if (!cancelled) {
          console.error('Failed to load law enforcement attachments', e);
        }
      } finally {
        if (!cancelled) {
          setLawAttachmentsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [claimData.claimId, watchedLossTypeId, lossTypes]);

  // Initialize form fields from Redux store when component mounts or when claim data changes
  useEffect(() => {
    setValue('incident_date', claimData.incident_date || '');
    setValue('tpp_loss_type_id', claimData.tpp_loss_type_id || '');
    setValue('other_loss_type_description', claimData.other_loss_type_description || '');
    setValue('authority_name', claimData.authority_name || '');
    setValue('authority_filed_report', claimData.authority_filed_report || '');
    setValue('items_current_location', claimData.items_current_location || '');
    setValue('info_manager_notified', claimData.info_manager_notified || '');
    setValue('another_claim_filed', claimData.another_claim_filed || '');
    setValue('another_claim_loss_type_id', claimData.another_claim_loss_type_id || '');
    setValue('another_claim_date', claimData.another_claim_date || '');
  }, [claimData, setValue]);

  // Re-apply loss type from Redux once options load (e.g. Back from Damages; API ids may be number vs string).
  useEffect(() => {
    const id = claimData.tpp_loss_type_id;
    if (!id || lossTypes.length === 0) return;
    if (lossTypes.some((type) => String(type.id) === String(id))) {
      setValue('tpp_loss_type_id', String(id), { shouldValidate: false });
    }
  }, [lossTypes, claimData.tpp_loss_type_id, setValue]);

  useEffect(() => {
    void trigger([
      'other_loss_type_description',
      'authority_name',
      'authority_filed_report',
      'law_enforcement_reports',
    ]);
  }, [watchedLossTypeId, lossTypes.length, existingLawReports.length, trigger]);

  const handleConfirmDeleteAllLawReports = async () => {
    const claimId = claimData.claimId;
    if (!claimId) return;
    setIsDeletingAllLaw(true);
    try {
      await deleteAllLawEnforcementAttachments(claimId);
      setExistingLawReports([]);
      setValue('law_enforcement_reports', undefined, { shouldValidate: true, shouldDirty: true });
      void trigger('law_enforcement_reports');
      setDeleteAllLawDialogOpen(false);
    } catch (err) {
      showError(getErrorMessage(err) || 'Failed to delete law enforcement reports.');
    } finally {
      setIsDeletingAllLaw(false);
    }
  };

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    const claimId = claimData.claimId;
    if (!claimId) {
      showError('Please start from the beginning.');
      navigate('/', { replace: true });
      return;
    }
    setIsSubmitting(true);
    try {
      const incidentDateYyyyMmDd = toYyyyMmDd(data.incident_date) ?? data.incident_date;
      const incidentPayload: Record<string, string> = {
        incident_date: incidentDateYyyyMmDd,
        tpp_loss_type_id: data.tpp_loss_type_id,
        other_loss_type_description: (data.other_loss_type_description || '').trim(),
        authority_name: data.authority_name || '',
        authority_filed_report: data.authority_filed_report || '',
        items_current_location: data.items_current_location,
        info_manager_notified: data.info_manager_notified,
        another_claim_filed: data.another_claim_filed,
        another_claim_loss_type_id: data.another_claim_filed === '1' ? (data.another_claim_loss_type_id || '') : '',
        another_claim_date: data.another_claim_filed === '1' ? (data.another_claim_date || '') : '',
      };
      dispatch(updateClaimField({ field: 'incident_date', value: data.incident_date }));
      dispatch(updateClaimField({ field: 'tpp_loss_type_id', value: data.tpp_loss_type_id }));
      dispatch(updateClaimField({
        field: 'other_loss_type_description',
        value: (data.other_loss_type_description || '').trim(),
      }));
      dispatch(updateClaimField({ field: 'authority_name', value: data.authority_name || '' }));
      dispatch(updateClaimField({ field: 'authority_filed_report', value: data.authority_filed_report || '' }));
      dispatch(updateClaimField({ field: 'items_current_location', value: data.items_current_location }));
      dispatch(updateClaimField({ field: 'info_manager_notified', value: data.info_manager_notified }));
      dispatch(updateClaimField({ field: 'another_claim_filed', value: data.another_claim_filed }));
      dispatch(updateClaimField({
        field: 'another_claim_loss_type_id',
        value: data.another_claim_filed === '1' ? (data.another_claim_loss_type_id || '') : '',
      }));
      dispatch(updateClaimField({
        field: 'another_claim_date',
        value: data.another_claim_filed === '1' ? (data.another_claim_date || '') : '',
      }));

      await updateClaim(claimId, incidentPayload);

      if (data.law_enforcement_reports instanceof FileList && data.law_enforcement_reports.length > 0) {
        const files = Array.from(data.law_enforcement_reports);
        await uploadClaimAttachments(claimId, files, 'law_enforcement_report');
      }
      navigate('/damages', { state: { openAddInventoryIfEmpty: true } });
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      showError(errorMessage || 'Failed to save incident details. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
            <div className="banner">
        <img src={banner} alt="TPP Claims Banner" />
        <div className="banner-content">
          <h1>File a Claim</h1>
          <h2>Tenant Protection Plan</h2>
          <p>Follow our easy-to-use claim process to submit your claim!</p>
        </div>
      </div>

      {/* Progress Indicator */}
      <ProgressSteps currentStep="incident" />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" gutterBottom={false}>
          Incident Details
        </Typography>
      </Box>
      <Box 
        component="form" 
        onSubmit={handleSubmit(onSubmit, (submitErrors) => {
          const priority: (keyof FormData)[] = [
            'incident_date',
            'tpp_loss_type_id',
            'other_loss_type_description',
            'authority_name',
            'authority_filed_report',
            'law_enforcement_reports',
            'items_current_location',
            'info_manager_notified',
            'another_claim_filed',
            'another_claim_loss_type_id',
            'another_claim_date',
          ];
          for (const key of priority) {
            const msg = submitErrors[key]?.message;
            if (msg) {
              showError(String(msg));
              return;
            }
          }
          if (Object.keys(submitErrors).length > 0) {
            showError('Please correct the form errors before continuing.');
          }
        })}
        sx={{ 
          mt: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 3
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          All fields are required unless marked as optional.
        </Typography>
        <div className="form-row">
          <div className="form-group">
            <Controller
              name="tpp_loss_type_id"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.tpp_loss_type_id}>
                  <InputLabel>Reason for Claim</InputLabel>
                  <Select
                    {...field}
                    label="Reason for Claim"
                    value={
                      lossTypes.length === 0
                        ? ''
                        : lossTypeIdInList(field.value || '')
                          ? String(field.value)
                          : ''
                    }
                    onChange={(e) => {
                      const v = String(e.target.value);
                      field.onChange(v);
                      handleFieldChange('tpp_loss_type_id', v);
                      if (!isTheftOrVandalismLossType(v, lossTypes)) {
                        setValue('authority_name', '', { shouldValidate: true, shouldDirty: true });
                        setValue('authority_filed_report', '', { shouldValidate: true, shouldDirty: true });
                        setValue('law_enforcement_reports', undefined, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        handleFieldChange('authority_name', '');
                        handleFieldChange('authority_filed_report', '');
                      }
                      if (!isOtherLossType(v, lossTypes)) {
                        setValue('other_loss_type_description', '', { shouldValidate: true, shouldDirty: true });
                        handleFieldChange('other_loss_type_description', '');
                      }
                      void trigger([
                        'other_loss_type_description',
                        'authority_name',
                        'authority_filed_report',
                        'law_enforcement_reports',
                      ]);
                      if (isTheftOrVandalismLossType(v, lossTypes)) {
                        setPoliceReportInfoDialogOpen(true);
                      }
                      if (isPestLossType(v, lossTypes)) {
                        setPestPayoutDialogOpen(true);
                      }
                    }}
                  >
                    {lossTypes.length === 0 ? (
                      <MenuItem value="">
                        <em>Loading...</em>
                      </MenuItem>
                    ) : (
                      lossTypes.map((type) => (
                        <MenuItem key={type.id} value={String(type.id)}>
                          {type.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {errors.tpp_loss_type_id && (
                    <Typography color="error" variant="caption">
                      {errors.tpp_loss_type_id.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
          </div>
          <div className="form-group">
            <IncidentDateField
              control={control}
              errors={errors}
              handleFieldChange={handleFieldChange}
            />
          </div>
        </div>

        {isOtherLoss && (
          <div className="form-row">
            <div className="form-group">
              <Controller
                name="other_loss_type_description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Other Loss Type Description"
                    error={!!errors.other_loss_type_description}
                    helperText={errors.other_loss_type_description?.message}
                    InputLabelProps={{
                      shrink: !!field.value,
                    }}
                    onChange={(e) => {
                      field.onChange(e);
                      handleFieldChange('other_loss_type_description', e.target.value);
                    }}
                  />
                )}
              />
            </div>
          </div>
        )}

        <div className="form-row">
          <div className="form-group">
            <Controller
              name="items_current_location"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Current location of damaged items"
                  error={!!errors.items_current_location}
                  helperText={errors.items_current_location?.message}
                  InputLabelProps={{
                    shrink: !!field.value
                  }}
                  onChange={(e) => {
                    field.onChange(e);
                    handleFieldChange('items_current_location', e.target.value);
                  }}
                />
              )}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <Controller
              name="info_manager_notified"
              control={control}
              render={({ field }) => (
                <FormControl component="fieldset" error={!!errors.info_manager_notified}>
                  <Typography><strong>Was the property manager notified of the loss or damage?</strong></Typography>
                  <RadioGroup
                    row
                    value={field.value || ''}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      handleFieldChange('info_manager_notified', e.target.value);
                    }}
                  >
                    <FormControlLabel value="1" control={<Radio />} label="Yes" />
                    <FormControlLabel value="0" control={<Radio />} label="No" />
                  </RadioGroup>
                  {errors.info_manager_notified && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      {errors.info_manager_notified.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
          </div>
        </div>

        {isTheftOrVandalism && (
          <>
            <div className="form-row">
              <div className="form-group">
                <Controller
                  name="authority_name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Law Enforcement Name"
                      required
                      error={!!errors.authority_name}
                      helperText={errors.authority_name?.message}
                      InputLabelProps={{
                        shrink: !!field.value
                      }}
                      onChange={(e) => {
                        field.onChange(e);
                        handleFieldChange('authority_name', e.target.value);
                      }}
                    />
                  )}
                />
              </div>
              <div className="form-group">
                <Controller
                  name="authority_filed_report"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Filed Report #"
                      required
                      error={!!errors.authority_filed_report}
                      helperText={errors.authority_filed_report?.message}
                      InputLabelProps={{
                        shrink: !!field.value
                      }}
                      onChange={(e) => {
                        field.onChange(e);
                        handleFieldChange('authority_filed_report', e.target.value);
                      }}
                    />
                  )}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Law Enforcement Reports.{' '}
                  <Box component="span" sx={{ fontWeight: 700, color: 'var(--primary-color)' }}>
                    Required for Theft/Vandalism.
                  </Box>
                  {' '}
                  Ok to upload multiple files at one time.
                </Typography>
                {lawAttachmentsLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                    <CircularProgress size={22} />
                    <Typography variant="body2" color="text.secondary">
                      Loading uploaded reports…
                    </Typography>
                  </Box>
                ) : existingLawReports.length > 0 ? (
                  <Box
                    sx={{
                      p: 2,
                      border: '1px dashed',
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: 'action.hover',
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                      Law Enforcement Reports
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1 }}>
                      {claimData.claimId != null
                        ? existingLawReports.map((att) => (
                            <LawEnforcementReportThumb key={att.id} claimId={claimData.claimId as number} att={att} />
                          ))
                        : null}
                    </Box>
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() => setDeleteAllLawDialogOpen(true)}
                      sx={{ cursor: 'pointer', textDecoration: 'underline', border: 'none', background: 'none', font: 'inherit', p: 0 }}
                    >
                      Delete All
                    </Link>
                  </Box>
                ) : (
                  <>
                    <Controller
                      name="law_enforcement_reports"
                      control={control}
                      render={({ field: { onChange, onBlur, name, ref } }) => (
                        <Box>
                          <input
                            ref={(el) => {
                              lawEnforcementFileInputRef.current = el;
                              ref(el);
                            }}
                            name={name}
                            id="law-enforcement-reports"
                            type="file"
                            multiple
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            onChange={(e) => {
                              const files = (e.target as HTMLInputElement).files;
                              onChange(files || undefined);
                              void trigger('law_enforcement_reports');
                            }}
                            onBlur={onBlur}
                            style={{
                              width: '100%',
                              maxWidth: '100%',
                              marginBottom: 4,
                              fontSize: 16,
                              boxSizing: 'border-box',
                            }}
                          />
                          {watchedLawEnforcementFiles instanceof FileList &&
                            watchedLawEnforcementFiles.length > 0 && (
                              <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                {Array.from(watchedLawEnforcementFiles)
                                  .map((f) => f.name)
                                  .join(', ')}
                              </Typography>
                            )}
                          {errors.law_enforcement_reports && (
                            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                              {String(errors.law_enforcement_reports.message ?? '')}
                            </Typography>
                          )}
                        </Box>
                      )}
                    />
                    <Typography variant="caption" color="textSecondary">
                      Accepted formats: PDF, DOC, DOCX, JPG, JPEG, PNG. (No videos yet. You will do that in the next
                      step.)
                    </Typography>
                  </>
                )}
              </div>
            </div>

            <Dialog
              open={deleteAllLawDialogOpen}
              onClose={() => !isDeletingAllLaw && setDeleteAllLawDialogOpen(false)}
            >
              <DialogTitle>Delete law enforcement reports?</DialogTitle>
              <DialogContent>
                <Typography variant="body2">
                  Are you sure you want to delete all of the Law Enforcement Reports?
                </Typography>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={() => setDeleteAllLawDialogOpen(false)} disabled={isDeletingAllLaw}>
                  No
                </Button>
                <Button
                  onClick={() => void handleConfirmDeleteAllLawReports()}
                  color="error"
                  variant="contained"
                  disabled={isDeletingAllLaw}
                >
                  {isDeletingAllLaw ? <CircularProgress size={22} color="inherit" /> : 'Yes'}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}

        <div className="form-row">
          <div className="form-group">
            <Controller
              name="another_claim_filed"
              control={control}
              render={({ field }) => (
                <FormControl component="fieldset" sx={{ width: '100%' }} error={!!errors.another_claim_filed}>
                  <Typography><strong>Have you filed any other claims?</strong></Typography>
                  <RadioGroup
                    row
                    value={field.value || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      field.onChange(value);
                      handleFieldChange('another_claim_filed', value);
                      if (value === '0') {
                        setValue('another_claim_loss_type_id', '', { shouldValidate: true, shouldDirty: true });
                        setValue('another_claim_date', '', { shouldValidate: true, shouldDirty: true });
                        handleFieldChange('another_claim_loss_type_id', '');
                        handleFieldChange('another_claim_date', '');
                      }
                    }}
                  >
                    <FormControlLabel value="1" control={<Radio />} label="Yes" />
                    <FormControlLabel value="0" control={<Radio />} label="No" />
                  </RadioGroup>
                  {errors.another_claim_filed && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      {errors.another_claim_filed.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
          </div>
        </div>

        {watchedAnotherClaimFiled === '1' && (
          <div className="form-row">
            <div className="form-group">
              <Controller
                name="another_claim_loss_type_id"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.another_claim_loss_type_id}>
                    <InputLabel>If yes, what type of loss?</InputLabel>
                    <Select
                      {...field}
                      label="If yes, what type of loss?"
                      value={
                        lossTypes.length === 0
                          ? ''
                          : lossTypeIdInList(field.value || '')
                            ? String(field.value)
                            : ''
                      }
                      onChange={(e) => {
                        const value = String(e.target.value);
                        field.onChange(value);
                        handleFieldChange('another_claim_loss_type_id', value);
                      }}
                    >
                      {lossTypes.length === 0 ? (
                        <MenuItem value="">
                          <em>Loading...</em>
                        </MenuItem>
                      ) : (
                        lossTypes.map((type) => (
                          <MenuItem key={type.id} value={String(type.id)}>
                            {type.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    {errors.another_claim_loss_type_id && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                        {errors.another_claim_loss_type_id.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </div>
            <div className="form-group">
              <Controller
                name="another_claim_date"
                control={control}
                render={({ field }) => (
                  <DateFieldWithCalendar
                    label="Date of other claim filed (mm/dd/yyyy format)"
                    value={field.value || ''}
                    onChange={(val) => {
                      field.onChange(val);
                      handleFieldChange('another_claim_date', val);
                    }}
                    error={!!errors.another_claim_date}
                    helperText={errors.another_claim_date?.message}
                    maxDate={new Date()}
                  />
                )}
              />
            </div>
          </div>
        )}

        <div className="form-row">
          <div className="form-group" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start', width: '100%' }}>
            {config.APP_MODE !== 'production' && (
              <button 
              type="button"
              onClick={() => {
                void (async () => {
                  const d = new Date();
                  const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                  const reportNumber = 'REP-' + Math.floor(1000 + Math.random() * 9000);
                  const theftLike = lossTypes.find((t) => {
                    const n = (t.name || '').toLowerCase();
                    return n.includes('theft') || n.includes('vandalism');
                  });
                  const otherLt = lossTypes.find((t) => (t.name || '').trim().toLowerCase() === 'other');
                  const lossTypeId = theftLike?.id ?? otherLt?.id ?? (lossTypes.length > 0 ? lossTypes[0].id : '1');
                  const officerName = 'Officer John Smith';
                  const otherDescSample = 'Tenant-reported other: water intrusion from neighboring unit plumbing.';

                  // Update Redux store
                  handleFieldChange('tpp_loss_type_id', lossTypeId);
                  handleFieldChange('incident_date', today);
                  if (isOtherLossType(String(lossTypeId), lossTypes)) {
                    handleFieldChange('other_loss_type_description', otherDescSample);
                    handleFieldChange('authority_name', '');
                    handleFieldChange('authority_filed_report', '');
                  } else {
                    handleFieldChange('other_loss_type_description', '');
                    handleFieldChange('authority_name', officerName);
                    handleFieldChange('authority_filed_report', reportNumber);
                  }
                  handleFieldChange('items_current_location', '123 Main St, Miami, FL 33101');
                  handleFieldChange('info_manager_notified', '1');
                  handleFieldChange('another_claim_filed', '1');
                  handleFieldChange('another_claim_loss_type_id', lossTypeId);
                  const oneMonthAgo = new Date();
                  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
                  const formattedDate = `${oneMonthAgo.getFullYear()}-${String(oneMonthAgo.getMonth() + 1).padStart(2, '0')}-${String(oneMonthAgo.getDate()).padStart(2, '0')}`;
                  handleFieldChange('another_claim_date', formattedDate);

                  // Update form fields
                  setValue('tpp_loss_type_id', lossTypeId, { shouldValidate: true, shouldDirty: true });
                  setValue('incident_date', today, { shouldValidate: true, shouldDirty: true });
                  if (isOtherLossType(String(lossTypeId), lossTypes)) {
                    setValue('other_loss_type_description', otherDescSample, { shouldValidate: true, shouldDirty: true });
                    setValue('authority_name', '', { shouldValidate: true, shouldDirty: true });
                    setValue('authority_filed_report', '', { shouldValidate: true, shouldDirty: true });
                  } else {
                    setValue('other_loss_type_description', '', { shouldValidate: true, shouldDirty: true });
                    setValue('authority_name', officerName, { shouldValidate: true, shouldDirty: true });
                    setValue('authority_filed_report', reportNumber, { shouldValidate: true, shouldDirty: true });
                  }
                  setValue('items_current_location', '123 Main St, Miami, FL 33101', { shouldValidate: true, shouldDirty: true });
                  setValue('info_manager_notified', '1', { shouldValidate: true, shouldDirty: true });
                  setValue('another_claim_filed', '1', { shouldValidate: true, shouldDirty: true });
                  setValue('another_claim_loss_type_id', lossTypeId, { shouldValidate: true, shouldDirty: true });
                  setValue('another_claim_date', formattedDate, { shouldValidate: true, shouldDirty: true });

                  if (isTheftOrVandalismLossType(String(lossTypeId), lossTypes)) {
                    try {
                      const res = await fetch(testPoliceReportPng);
                      if (!res.ok) throw new Error(`Failed to load test police report: ${res.status}`);
                      const buf = await res.arrayBuffer();
                      const dt = new DataTransfer();
                      dt.items.add(new File([buf], 'test-police-report.png', { type: 'image/png' }));
                      setValue('law_enforcement_reports', dt.files, { shouldValidate: true, shouldDirty: true });
                      void trigger('law_enforcement_reports');
                    } catch {
                      showError('Could not load the test law enforcement report image.');
                    }
                    setPoliceReportInfoDialogOpen(true);
                  } else {
                    setValue('law_enforcement_reports', undefined, { shouldValidate: true, shouldDirty: true });
                    const input = lawEnforcementFileInputRef.current;
                    if (input) input.value = '';
                    void trigger('law_enforcement_reports');
                  }

                  if (isPestLossType(String(lossTypeId), lossTypes)) {
                    setPestPayoutDialogOpen(true);
                  }
                })();
              }}
              
              
              
              style={{ textDecoration: 'underline', cursor: 'pointer', marginTop: '8px', background: 'none', border: 'none', padding: 0, color: 'inherit', font: 'inherit' }}>
                Load Test Data
              </button>
            )}
          </div>
        </div>

        <div className="form-row">
          <Box sx={{ display: 'flex', gap: 2, mt: 2, width: '100%' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/', { state: { fromIncidentBack: true } })}
              disabled={isSubmitting}
              sx={{ 
                color: 'var(--primary-color)',
                borderColor: 'var(--primary-color)',
                '&:hover': {
                  borderColor: 'var(--primary-color)',
                  backgroundColor: 'rgba(var(--primary-color-rgb), 0.04)'
                }
              }}
              fullWidth
            >
              Back
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              sx={{ 
                backgroundColor: 'var(--primary-color)',
                '&:hover': {
                  backgroundColor: 'var(--primary-color)',
                  opacity: 0.9
                }
              }}
              fullWidth
            >
              {isSubmitting ? (
                <CircularProgress size={24} />
              ) : (
                <>
                  <span className="form-submit-label--desktop">Continue to Inventory</span>
                  <span className="form-submit-label--mobile">Continue</span>
                </>
              )}
            </Button>
          </Box>
        </div>

      </Box>

      <Dialog
        open={pestPayoutDialogOpen}
        onClose={() => setPestPayoutDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="pest-payout-dialog-title"
      >
        <DialogTitle id="pest-payout-dialog-title">Pest-related claims</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
          Please refer to the TPP Addendum for pest coverage information
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            onClick={() => setPestPayoutDialogOpen(false)}
            autoFocus
            sx={{
              backgroundColor: 'var(--primary-color)',
              '&:hover': { backgroundColor: 'var(--primary-color)', opacity: 0.9 },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={policeReportInfoDialogOpen}
        onClose={() => setPoliceReportInfoDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="police-report-info-dialog-title"
      >
        <DialogTitle id="police-report-info-dialog-title">Police Report</DialogTitle>
        <DialogContent>
          <Typography variant="body2" component="div">
            {`ALL Theft/Vandalism claims require a police report.  Please note that we are unable to obtain the report on your behalf because we are not listed as the victim in the case. A copy of your finalized (\u201capproved\u201d) police report is required. The report must identify you as the victim and include the following details:`}
          </Typography>
          <Box component="ul" sx={{ pl: 2.5, my: 2, mb: 1 }}>
            <Typography component="li" variant="body2" sx={{ display: 'list-item' }}>
              The method of entry or break-in to your unit
            </Typography>
            <Typography component="li" variant="body2" sx={{ display: 'list-item', mt: 1 }}>
              A list of the personal property stolen, including estimated dollar values
            </Typography>
            <Typography component="li" variant="body2" sx={{ display: 'list-item', mt: 1 }}>
              The responding or assigned officer&apos;s narrative/details of the incident
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            This information is vital and we will not be able to process your claim if missing.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            onClick={() => setPoliceReportInfoDialogOpen(false)}
            autoFocus
            sx={{
              backgroundColor: 'var(--primary-color)',
              '&:hover': { backgroundColor: 'var(--primary-color)', opacity: 0.9 },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Step1CompleteOverlayDialog
        open={step1OverlayOpen}
        claimNumber={step1OverlayClaimNumber}
        onOk={handleStep1OverlayOk}
      />
    </Layout>
  );
};

export default Incident;

